import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AllStak } from '@allstak/react';
import { Button, Input } from '@/shared/ui/primitives';
import { LockOutlined, MailOutlined } from '@/shared/ui/icons';
import apiClient from '@/lib/apiClient';

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const next: typeof errors = {};
    if (!email) next.email = t('login.emailRequired');
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) next.email = t('login.emailRequired');
    if (!password) next.password = t('login.passwordRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await apiClient.post('/admin/login', { email, password });
      const admin = res?.data?.admin ?? res?.data?.user ?? null;
      AllStak.setUser({
        id: admin?.id != null ? String(admin.id) : email,
        email: admin?.email ?? email,
      });
      AllStak.addBreadcrumb('auth', 'Admin login succeeded', 'info');
      toast.success(t('login.success'));
      window.location.href = '/';
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ds-login-bg">
      <form className="ds-login-card" onSubmit={handleSubmit}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div
            style={{
              width: 56, height: 56, margin: '0 auto 12px',
              borderRadius: 'var(--r-lg)',
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary-dark))',
              display: 'grid', placeItems: 'center',
              color: '#fff', fontWeight: 800, fontSize: 24,
              boxShadow: 'var(--shadow-md)',
            }}
          >
            ش
          </div>
          <h1 className="ds-title ds-title-3" style={{ marginBottom: 4 }}>{t('app.title')}</h1>
          <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{t('app.admin')}</div>
        </div>

        <div className="ds-form">
          <div className="ds-form-item">
            <label className="ds-form-label required">{t('login.email')}</label>
            <Input
              prefix={<MailOutlined />}
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              status={errors.email ? 'error' : undefined}
              autoComplete="username"
              data-allstak-mask="true"
              data-sensitive="true"
              autoFocus
            />
            {errors.email && <div className="ds-form-error">{errors.email}</div>}
          </div>

          <div className="ds-form-item">
            <label className="ds-form-label required">{t('login.password')}</label>
            <Input.Password
              prefix={<LockOutlined />}
              value={password}
              onChange={(e: any) => setPassword(e.target.value)}
              placeholder="••••••••"
              status={errors.password ? 'error' : undefined}
              autoComplete="current-password"
            />
            {errors.password && <div className="ds-form-error">{errors.password}</div>}
          </div>

          <Button type="primary" htmlType="submit" loading={loading} size="large" block>
            {t('login.signIn')}
          </Button>
        </div>
      </form>
    </div>
  );
}
